/**
 * Read/write adapter between the real `payroll.listPayItems` /
 * `payroll.createPayItem` / `payroll.updatePayItem` wire shapes and the UI's
 * `Allowance`/`Deduction` types (#22728). Pure, no React — mirrors
 * `wagepro_store_employees.ts`'s `RealEmployeeRecord`/
 * `deriveEmployeesFromRecords` pattern: `RealPayItemRecord` is the permanent
 * adapter shape, `deriveAllowancesFromRecords`/`deriveDeductionsFromRecords`
 * are the permanent read seams, and `buildCreatePayItemInput`/
 * `buildUpdatePayItemInput` are the permanent write seams `use_data_centre.ts`
 * calls.
 *
 * ── One catalog, two tabs ───────────────────────────────────────────────────
 *
 * The Data Centre's Allowances and Deductions tabs both read from the SAME
 * `payroll.listPayItems` catalog (`pay_items`, #3365 Xero parity) — one fetch,
 * split client-side by `category`: `'earnings'` rows become `Allowance`s,
 * `'deduction'` rows become `Deduction`s. `'benefit'`/`'leave'` rows are
 * fetched but not surfaced by either tab (out of scope for this pass).
 * `addDeduction`/`removeDeduction`/`assignPayItem`/`listEmployeePayItems`/
 * `updateEmployeePayItem`/`removeEmployeePayItem` are deliberately never
 * called here — this screen operates entirely at the entity-catalog level,
 * never at the per-employee-assignment level (see PR #22749's own commit
 * message for the same boundary on the backend side).
 *
 * ── Statutory EPF/ETF/APIT never appear in this real view (documented gap) ──
 *
 * The mock `Deduction` fixtures included EPF/ETF/APIT with `statutory: true`
 * (`mock_data.ts`, now deleted). The real `pay_items` table has no
 * `statutory` column and `createPayItem`'s Zod input has no way to set one —
 * those three contribution types are computed automatically each pay run by
 * the SL statutory engine (`packages/jumpstone-core`'s jurisdiction packs +
 * `apps/api/src/services/*statutory*`), never stored as a tenant-editable
 * `pay_items` row. So every `Deduction` this adapter produces is, by
 * construction, tenant-created (`statutory: false` always) — this is not a
 * simplification, it is the accurate real-data answer: there is nothing
 * statutory to show on THIS catalog-level screen. A firm that wants to see
 * the statutory rates themselves uses the Payroll Policies screen instead
 * (separate child issue, #22726). The Deductions tab's Type column
 * consequently always reads "Voluntary" for real data today — an accepted,
 * documented limitation (adversarial review, #22728), not a bug: it is a
 * true statement about every row this adapter can ever produce.
 *
 * ── Rule/basis derivation is lossy by construction (documented gaps) ────────
 *
 * `pay_items` has fields to describe a rate (`rate_type`, `rate_cents`,
 * `rate_bps`) plus `apit_treatment` — fewer dimensions than the
 * mock's bespoke `AllowanceRule`/`DeductionRule` unions had.
 * This adapter derives the closest real-data-backed rule rather than
 * inventing fields the backend doesn't have:
 *
 * - `rate_type: 'percentage'` -> `percent_of_basic` (both allowances and
 *   deductions), `rate: rate_bps ?? 0`.
 * - `rate_type: 'per_hour'` (both allowances AND deductions, #22834 — the
 *   backend column applies with no category restriction, see "Write path"
 *   below) -> `hourly_rate`, `amount: rate_cents ?? manager_input`.
 *   `rate_cents` is the REAL column for a per-hour rate — its own schema doc
 *   comment says "Rate in minor units per hour (for per_hour rate_type), or
 *   flat amount"
 *   (`apps/api/src/db/schema/tenant/payroll.ts`), and the pay-run engine
 *   (`computeHourlyGrossCents`, `payroll_pay_run_calculations.ts`) reads
 *   `rate_cents` for `per_hour` items too. `rate_bps` is NEVER read for
 *   `per_hour` — an earlier version of this adapter incorrectly read
 *   `rate_bps` here and rendered every real per-hour allowance as "1.0x
 *   hourly rate" regardless of its actual amount (adversarial review finding,
 *   #22728). `AllowanceRule`'s pre-existing `hourly_multiple` kind (a
 *   MULTIPLIER on top of a base hourly rate) has no backing column at all —
 *   it is never produced by this adapter, kept only because its i18n label
 *   still exists (mirrors `DeductionRule.progressive_bracket`'s identical
 *   never-produced status, see below).
 * - `rate_type: 'fixed'` with a recorded `rate_cents` -> `fixed_monthly`
 *   (allowances) / `fixed_installment` (deductions), carrying that exact
 *   amount. `rate_type: 'per_hour'` with a recorded `rate_cents` -> the same
 *   `hourly_rate` kind for BOTH allowances and deductions (#22834 -- deriving
 *   this for deductions too, not just allowances, is what makes the
 *   deduction drawer's edit-mode prefill show a real recorded amount instead
 *   of always collapsing to `manager_input`).
 * - `rate_type: 'fixed'` with NO recorded `rate_cents` (or `per_hour` with no
 *   recorded `rate_cents`) -> `manager_input` (both allowances AND
 *   deductions, #22834). This is the COMMON case, not an edge case: SL preset
 *   catalog rows are seeded with `rate_cents`/`rate_bps` both NULL by design
 *   (`pay_item_presets.ts`'s own doc comment — "rates are always an
 *   entity/assignment decision") since a firm sets the actual amount later.
 * - `progressive_bracket` (mock-only "APIT is on this list for reference")
 *   is NEVER produced by this adapter — see the statutory-gap note above;
 *   nothing in the real catalog represents it.
 * - There is deliberately NO deduction "basis" (pre-tax/post-tax) concept
 *   (#26642). `pay_items.is_pre_tax` is a reserved column that NO computation
 *   reads (see its schema doc comment in
 *   `apps/api/src/db/schema/tenant/payroll.ts`), and Sri Lankan APIT has no
 *   pre-tax deduction at all (IRD Table 1 para 03/07: tax is withheld
 *   "without deducting any sum" from regular profits). An earlier version of
 *   this adapter mapped `is_pre_tax` to a `'pre_tax'`/`'post_tax'` basis
 *   column, which told users a deduction lowers APIT when it never does. The
 *   field is not in {@link RealPayItemRecord}; the wire row may still carry
 *   it and it is ignored.
 * - `Allowance.taxable` is derived from `apit_treatment`, NOT `is_pre_tax` —
 *   `is_pre_tax` is unread by every computation; the real
 *   per-item earnings-taxability mechanism is `apit_treatment`
 *   (`'regular'`/`null` = taxed under the normal schedule, `'lumpsum'` =
 *   still taxed, routed through the EGAR lump-sum method, `'exempt'` = not
 *   taxed). An earlier version of this adapter read `is_pre_tax` for
 *   `taxable` instead — the wrong field entirely (adversarial review finding,
 *   #22728). `apit_treatment: 'exempt'` cannot actually be PERSISTED via
 *   `updatePayItem` today (it throws `BAD_REQUEST`, tracked as #22663 —
 *   cross-period IRD Table 5/lump-sum reconstruction assumes no exempt
 *   amounts exist yet), so every real earnings item is, in practice, always
 *   `taxable: true` until that lands — this adapter still derives it
 *   correctly from the real field (rather than a fabricated always-true
 *   constant) so it degrades correctly the moment #22663 ships. Because
 *   taxability cannot be toggled server-side today, the write path below
 *   never collects or sends a taxable/pre-tax value at all — see "Write
 *   path".
 *
 * ── Write path: name + rate (#22820 allowances, #22834 deductions) ──────────
 *
 * `AllowanceDrawer` used to also collect a "Taxable" toggle mapped onto
 * `is_pre_tax` — removed (adversarial review, #22728): `is_pre_tax` is a
 * field the real pay-run engine never reads (for any category, #26642), so
 * that toggle was writing a dead field that could never actually change an
 * allowance's real taxability. The genuine mechanism (`apit_treatment`) can
 * only ever be set to `'regular'`/`'lumpsum'`/`null` via `updatePayItem`
 * today (`'exempt'` is rejected, #22663), so there is no reachable
 * non-taxable state to offer a control for yet.
 *
 * The write seams below set `name` + the full rate config (#22820 — the
 * allowance drawer used to silently force every new allowance to
 * `rate_type: 'fixed'` with no rate regardless of what the user intended;
 * #22834 brought the deduction drawer up to the same parity — it had the
 * IDENTICAL bug, since it was built as a straight mirror of the allowance
 * drawer's pre-#22820 name-only shape). `AllowanceRateDraft` is the UI-level
 * discriminated shape `AllowanceDrawer`/`DeductionDrawer` both build from
 * their own rate-type selector + amount/percent field
 * (`allowance_drawer_validation.ts`'s `toAllowanceRateDraft` /
 * `deduction_drawer_validation.ts`'s `toDeductionRateDraft`) — see this
 * type's own doc comment for why ONE shared discriminated union backs both
 * drawers rather than a duplicated `DeductionRateDraft` union: verified
 * directly against `apps/api/src/trpc/routers/payroll.ts`, `createPayItem`/
 * `updatePayItem`'s `rate_type`/`rate_cents`/`rate_bps` fields apply with NO
 * `category` restriction (unlike `liability_account_id`, `benefit`-only, or
 * `epf_etf_liable`/`apit_treatment`, `earnings`-only) — the wire shape is
 * genuinely identical regardless of whether the pay item is an allowance or
 * a deduction. `buildCreatePayItemInput`/`buildUpdatePayItemInput`
 * (allowances) and `buildCreateDeductionPayItemInput`/
 * `buildUpdateDeductionPayItemInput` (deductions) all reuse the SAME
 * `createRateFields`/`updateRateFields` helpers below to map that shared
 * draft onto the exact wire shape each mutation needs — no
 * allowance-specific or deduction-specific branching exists inside either
 * helper. `createPayItem`'s `rate_cents`/`rate_bps` are `optional()` (NOT
 * `nullable()`), so the create builder OMITS whichever one doesn't apply;
 * `updatePayItem`'s are `nullable().optional()`, so the update builder
 * explicitly sends `null` for the one that doesn't apply — this is what lets
 * an edit switch a pay item from, say, `percentage` to `fixed` and have the
 * stale `rate_bps` actually clear, not linger. A `manager_input` selection
 * creates/updates with `rate_type: 'fixed'` and no rate at all (the same
 * real "manager fills it in later" shape the write path always produced
 * before #22820/#22834) and, on edit, explicitly clears any
 * previously-recorded rate.
 *
 * ── Untouched rate section round-trips unchanged (#22820 F1/F2, #22834) ─────
 *
 * An adversarial review of the allowance write path found a rename-only edit
 * could silently corrupt a record: `deriveAllowanceRule`/`deriveDeductionRule`
 * map ANY amount-less row (`fixed` or `per_hour`, both with a null
 * `rate_cents`) to the same `manager_input` UI state, and a save path that
 * always re-derives `rate_type: 'fixed'` from that collapsed state
 * regardless of which one the record actually was would silently flip a real
 * `per_hour` item to `fixed` on a pure rename. Separately, an item with an
 * explicit `rate_cents: 0`/`rate_bps: 0`/`null` prefilled into an
 * amount-bearing kind with a technically-invalid zero amount, blocking Save
 * even for a pure rename. `AllowanceDrawer`/`DeductionDrawer` both track
 * whether the user actually touched the rate section (kind selector or
 * amount/percent field) versus left it at its as-loaded prefill; when
 * untouched, `allowance_drawer_validation.ts`'s `resolveAllowanceRateDraft` /
 * `deduction_drawer_validation.ts`'s `resolveDeductionRateDraft` send the
 * ORIGINAL `AllowanceRateSnapshot`/`DeductionRateSnapshot` (`./types.ts`)
 * back via the shared `'unchanged'` `AllowanceRateDraft` kind instead of
 * re-deriving from the current selector state, and Save is never blocked on
 * the untouched section's validity.
 *
 * Unlike allowances (where this was a retrofit an adversarial review caught
 * post-ship, #22820 F1/F2), the deduction drawer (#22834) gets this fix from
 * day one — it is built alongside the rate-type/amount feature itself, not
 * bolted on after a corruption bug shipped. It does NOT call
 * `payroll.addDeduction`/`payroll.removeDeduction` — those operate on the
 * per-EMPLOYEE `employee_deductions` assignment table (employee_id,
 * effective_from/to, a fixed deduction_type enum), a different model
 * entirely from this screen's entity-level deduction catalog. See this
 * module's "One catalog, two tabs" section above.
 *
 * ── Reused vs. mirrored: the extraction judgment call (#22834) ──────────────
 *
 * `AllowanceRateDraft`/`createRateFields`/`updateRateFields` below are reused
 * VERBATIM for deductions (see `DeductionRateDraft`'s doc comment) — they are
 * 100% category-agnostic backend-wire-shape plumbing with zero
 * allowance-specific branching, so duplicating them into a
 * `DeductionRateDraft`/`createDeductionRateFields`/`updateDeductionRateFields`
 * trio would be pure, pointless duplication.
 *
 * The drawer-level UI pattern (`rate_touched` dirty-tracking, the rate-kind
 * `<StyledSelect>` + conditional `<MoneyInput>`/percent `<Input>`, the
 * validation gate) is instead MIRRORED, not extracted into a shared hook —
 * `deduction_drawer_validation.ts`/`deduction_drawer.tsx` are each their own
 * file, structurally identical to `allowance_drawer_validation.ts`/
 * `allowance_drawer.tsx` but not sharing code with them. This is deliberate:
 * the two drawers' rate-kind option arrays, i18n key namespaces
 * (`allowance_amount_label_fixed` vs. `deduction_amount_label_fixed`), and
 * field id/aria-label strings are independently-declared UI concerns, not a
 * shared backend contract — today's four option values happen to match, but
 * nothing guarantees they always will (e.g. a future deduction-only
 * `progressive_bracket`-editing UI, or an allowance-only option). Generalizing
 * a `usePayItemRateDraft` hook now, for exactly these two call sites, would
 * need speculative parameters (option set, i18n namespace, field ids) purely
 * to solve a duplication problem that doesn't cost anything today — each
 * drawer file is ~200 lines and reads correctly on its own. If a THIRD
 * sibling drawer ever needs this same treatment, that is the point to
 * revisit the hook extraction, not before.
 */
import type {
  Allowance,
  AllowanceRule,
  Deduction,
  DeductionRule,
} from './types';

/**
 * The wire shape `payroll.listPayItems` returns, narrowed to exactly the
 * fields this adapter reads. The real procedure returns the full Drizzle
 * `pay_items` row (more fields than this — `account_id`, `liability_account_id`,
 * `epf_etf_liable`, `is_archived`, timestamps — see
 * `apps/api/src/db/schema/tenant/payroll.ts`); this interface is
 * intentionally a subset, not a mirror, so it stays stable across
 * backend-only column additions.
 */
export interface RealPayItemRecord {
  id: string;
  category: 'earnings' | 'deduction' | 'benefit' | 'leave';
  name: string;
  rate_type: 'fixed' | 'per_hour' | 'percentage';
  rate_cents: number | null;
  rate_bps: number | null;
  /** Earnings-only in practice (`updatePayItem` rejects it for other categories); `null` on every other category. */
  apit_treatment: 'regular' | 'lumpsum' | 'exempt' | null;
}

function deriveAllowanceRule(record: RealPayItemRecord): AllowanceRule {
  if (record.rate_type === 'percentage') {
    return { kind: 'percent_of_basic', rate: record.rate_bps ?? 0 };
  }
  // rate_type === 'fixed' | 'per_hour' -- both store their rate in
  // rate_cents (see module docblock: rate_bps is never read here, that was
  // the fixed bug). An unset rate (the common seeded-preset case) is
  // manager_input regardless of which of the two rate_types it is.
  if (record.rate_cents !== null) {
    return record.rate_type === 'per_hour'
      ? { kind: 'hourly_rate', amount: record.rate_cents }
      : { kind: 'fixed_monthly', amount: record.rate_cents };
  }
  return { kind: 'manager_input' };
}

/**
 * Whether a real earnings pay item is taxable, from its ACTUAL statutory
 * field (`apit_treatment`) — see module docblock for why this replaced an
 * earlier, incorrect `is_pre_tax`-based derivation (the column is read by
 * nothing, #26642).
 */
function deriveTaxable(record: RealPayItemRecord): boolean {
  return record.apit_treatment !== 'exempt';
}

function toUiAllowance(record: RealPayItemRecord): Allowance {
  return {
    id: record.id,
    name: record.name,
    rule: deriveAllowanceRule(record),
    taxable: deriveTaxable(record),
    // #22820 F1 -- the exact triad, NOT re-derived from `rule` (see
    // `AllowanceRateSnapshot`'s doc comment, `./types.ts`, for why `rule`
    // alone is lossy).
    rate_snapshot: {
      rate_type: record.rate_type,
      rate_cents: record.rate_cents,
      rate_bps: record.rate_bps,
    },
  };
}

/**
 * Derives the Allowances tab's rows from the real `payroll.listPayItems`
 * result — every `'earnings'`-category row, in the order the backend
 * returned them. An explicitly empty array is a real, distinct state from
 * "no data yet" (see `use_data_centre.ts`'s `DataCentreState`) — a genuinely
 * allowance-less entity renders the screen's existing empty table, never a
 * fabricated mock row.
 */
export function deriveAllowancesFromRecords(
  records: readonly RealPayItemRecord[],
): Allowance[] {
  return records
    .filter((record) => record.category === 'earnings')
    .map(toUiAllowance);
}

/**
 * #22834: mirrors {@link deriveAllowanceRule} exactly (`progressive_bracket`
 * is never produced -- no statutory `pay_items` row exists to derive it
 * from, same as before). `DeductionRule.fixed_installment`/`hourly_rate` now
 * carry the real `rate_cents` amount, and an unset rate (the common
 * seeded-preset case) is `manager_input` regardless of which of the two
 * amount-bearing `rate_type`s it is -- see module docblock's "Rule/basis
 * derivation" section for why this parity with allowances matters (the
 * deduction drawer's edit-mode prefill needs a real amount to show).
 */
function deriveDeductionRule(record: RealPayItemRecord): DeductionRule {
  if (record.rate_type === 'percentage') {
    return { kind: 'percent_of_basic', rate: record.rate_bps ?? 0 };
  }
  if (record.rate_cents !== null) {
    return record.rate_type === 'per_hour'
      ? { kind: 'hourly_rate', amount: record.rate_cents }
      : { kind: 'fixed_installment', amount: record.rate_cents };
  }
  return { kind: 'manager_input' };
}

function toUiDeduction(record: RealPayItemRecord): Deduction {
  return {
    id: record.id,
    name: record.name,
    rule: deriveDeductionRule(record),
    // See module docblock: statutory EPF/ETF/APIT are never stored as a
    // tenant-editable pay_items row, so every real deduction this adapter
    // sees is, by construction, non-statutory.
    statutory: false,
    // #22834 (mirrors #22820 F1 for allowances) -- the exact triad, NOT
    // re-derived from `rule` (see `DeductionRateSnapshot`'s doc comment,
    // `./types.ts`, for why `rule` alone is lossy).
    rate_snapshot: {
      rate_type: record.rate_type,
      rate_cents: record.rate_cents,
      rate_bps: record.rate_bps,
    },
  };
}

/**
 * Derives the Deductions tab's rows from the real `payroll.listPayItems`
 * result — every `'deduction'`-category row. See
 * {@link deriveAllowancesFromRecords}'s doc comment for the same
 * "explicitly empty is real, never mock-filled" contract.
 */
export function deriveDeductionsFromRecords(
  records: readonly RealPayItemRecord[],
): Deduction[] {
  return records
    .filter((record) => record.category === 'deduction')
    .map(toUiDeduction);
}

/** The `{firm_id, entity_id}` pair every `payroll.*` call takes. */
export interface DataCentreEntityInput {
  firm_id: string;
  entity_id: string;
}

/**
 * The five ways a firm's save can resolve a pay item's rate — a UI-level
 * superset of the backend's 3-way `rate_type` enum. `'manager_input'`
 * represents `rate_type: 'fixed'` with no `rate_cents` recorded (see module
 * docblock's "Rule/basis derivation" section) — not a distinct backend enum
 * value. Discriminated so a `fixed`/`per_hour` selection cannot be
 * constructed without its `rate_cents`, and a `percentage` selection cannot
 * be constructed without its `rate_bps`.
 *
 * `'unchanged'` (#22820 F1/F2) is distinct from all four UI selections above:
 * it carries the exact `rate_type`/`rate_cents`/`rate_bps` triad an existing
 * pay item was loaded with, verbatim, for when the drawer's rate section was
 * never touched by the user (`allowance_drawer_validation.ts`'s
 * `resolveAllowanceRateDraft` / `deduction_drawer_validation.ts`'s
 * `resolveDeductionRateDraft`). It is what lets a pure rename of a
 * `per_hour`-with-null-`rate_cents` record round-trip as `per_hour` again,
 * rather than always collapsing to `manager_input`'s hardcoded `rate_type:
 * 'fixed'` the way every UI-derived selection above does. Never produced by
 * `toAllowanceRateDraft`/`toDeductionRateDraft` (the UI-selection mappers) —
 * only by `resolveAllowanceRateDraft`/`resolveDeductionRateDraft`, and only
 * in edit mode with a real snapshot to preserve.
 *
 * Named `AllowanceRateDraft` for historical reasons (#22820 shipped it for
 * allowances first) but used VERBATIM by deductions too (#22834) via the
 * {@link DeductionRateDraft} alias below — this type has no
 * allowance-specific field or branch anywhere; it is exactly the
 * `rate_type`/`rate_cents`/`rate_bps` triad `pay_items` needs regardless of
 * `category`. See module docblock's "Reused vs. mirrored" section for the
 * full extraction rationale.
 */
export type AllowanceRateDraft =
  | { kind: 'fixed'; rate_cents: number }
  | { kind: 'per_hour'; rate_cents: number }
  | { kind: 'percentage'; rate_bps: number }
  | { kind: 'manager_input' }
  | {
      kind: 'unchanged';
      rate_type: 'fixed' | 'per_hour' | 'percentage';
      rate_cents: number | null;
      rate_bps: number | null;
    };

/**
 * `DeductionDrawer`'s own name for {@link AllowanceRateDraft} (#22834) — a
 * plain alias, not a separate type, since the two categories' rate config is
 * byte-for-byte identical (see that type's doc comment). Exists so
 * `deduction_drawer_validation.ts`/`DeductionDraft` read as their own domain
 * concept at their call sites rather than importing an "Allowance"-named
 * type into deduction code.
 */
export type DeductionRateDraft = AllowanceRateDraft;

/**
 * What `AllowanceDrawer` actually collects from the user — name + rate (see
 * module docblock's "Write path" section for why `taxable` is still never
 * collected here). `id: null` means "creating a new allowance"; a real id
 * means "editing this existing one" — a required, explicit discriminator
 * rather than inferred from a client-fabricated id.
 */
export interface AllowanceDraft {
  id: string | null;
  name: string;
  rate: AllowanceRateDraft;
}

/**
 * What `DeductionDrawer` collects from the user — name + rate (#22834,
 * mirrors `AllowanceDraft` exactly; the pre-#22834 shape only ever set
 * `name`, see module docblock's "Write path" section). Kept as its own named
 * interface (rather than a bare alias used directly) so the deduction write
 * path reads as its own domain concept at call sites.
 */
export interface DeductionDraft {
  id: string | null;
  name: string;
  rate: DeductionRateDraft;
}

/** The exact `payroll.createPayItem` input shape this adapter uses (see `apps/api/src/trpc/routers/payroll.ts`). `rate_cents`/`rate_bps` are OMITTED (never `null`) when not applicable — `createPayItem`'s zod input only accepts `optional()` for them, not `nullable()`. */
export interface CreatePayItemInput {
  firm_id: string;
  entity_id: string;
  category: 'earnings' | 'deduction' | 'benefit' | 'leave';
  name: string;
  rate_type: 'fixed' | 'per_hour' | 'percentage';
  rate_cents?: number;
  rate_bps?: number;
}

/**
 * The exact `payroll.updatePayItem` input shape this adapter uses (see
 * `apps/api/src/trpc/routers/payroll.ts`). `updatePayItem`'s zod input
 * accepts `nullable().optional()` for `rate_type`/`rate_cents`/`rate_bps`,
 * and this interface mirrors that exactly — optional, not required. Both
 * `buildUpdatePayItemInput` (allowances, #22820) and
 * `buildUpdateDeductionPayItemInput` (deductions, #22834) now ALWAYS send
 * all three, one rate field explicitly `null`, so an edit can CLEAR a stale
 * rate left over from a previous rate-type selection (see
 * {@link updateRateFields}'s own doc comment) — kept optional on the
 * interface itself only because the fields are genuinely absent from the
 * wire type until a rate is resolved, not because either caller omits them
 * in practice.
 */
export interface UpdatePayItemInput {
  firm_id: string;
  entity_id: string;
  pay_item_id: string;
  name: string;
  rate_type?: 'fixed' | 'per_hour' | 'percentage';
  rate_cents?: number | null;
  rate_bps?: number | null;
}

/** The `rate_type`/`rate_cents`/`rate_bps` triad {@link buildCreatePayItemInput} needs for a given rate selection — see that interface's doc comment for why `rate_cents`/`rate_bps` are omitted, never `null`, here. */
function createRateFields(rate: AllowanceRateDraft): {
  rate_type: 'fixed' | 'per_hour' | 'percentage';
  rate_cents?: number;
  rate_bps?: number;
} {
  switch (rate.kind) {
    case 'fixed':
      return { rate_type: 'fixed', rate_cents: rate.rate_cents };
    case 'per_hour':
      return { rate_type: 'per_hour', rate_cents: rate.rate_cents };
    case 'percentage':
      return { rate_type: 'percentage', rate_bps: rate.rate_bps };
    case 'manager_input':
      return { rate_type: 'fixed' };
    case 'unchanged':
      // CREATE never has an original to preserve -- `unchanged` is only ever
      // produced for an EDIT with a loaded `AllowanceRateSnapshot`
      // (`resolveAllowanceRateDraft`), so this branch is unreachable in
      // practice. Handled anyway since `AllowanceRateDraft` is shared between
      // the create/update builders: sends the triad verbatim, OMITTING
      // whichever of rate_cents/rate_bps is null (createPayItem's zod input
      // is optional(), not nullable() -- see this function's doc comment).
      return {
        rate_type: rate.rate_type,
        ...(rate.rate_cents !== null ? { rate_cents: rate.rate_cents } : {}),
        ...(rate.rate_bps !== null ? { rate_bps: rate.rate_bps } : {}),
      };
  }
}

/** Same as {@link createRateFields} but for {@link buildUpdatePayItemInput} — see `UpdatePayItemInput`'s doc comment for why this one always sends both fields, one of them explicitly `null`. */
function updateRateFields(rate: AllowanceRateDraft): {
  rate_type: 'fixed' | 'per_hour' | 'percentage';
  rate_cents: number | null;
  rate_bps: number | null;
} {
  switch (rate.kind) {
    case 'fixed':
      return {
        rate_type: 'fixed',
        rate_cents: rate.rate_cents,
        rate_bps: null,
      };
    case 'per_hour':
      return {
        rate_type: 'per_hour',
        rate_cents: rate.rate_cents,
        rate_bps: null,
      };
    case 'percentage':
      return {
        rate_type: 'percentage',
        rate_cents: null,
        rate_bps: rate.rate_bps,
      };
    case 'manager_input':
      return { rate_type: 'fixed', rate_cents: null, rate_bps: null };
    case 'unchanged':
      // The exact original triad, verbatim -- see `AllowanceRateDraft`'s
      // doc comment. This is the actual F1 fix: an untouched rate section on
      // an edit sends this back instead of always re-deriving `rate_type:
      // 'fixed'` the way a `manager_input` selection does.
      return {
        rate_type: rate.rate_type,
        rate_cents: rate.rate_cents,
        rate_bps: rate.rate_bps,
      };
  }
}

/**
 * Builds the `payroll.createPayItem` input for a brand-new allowance —
 * always `category: 'earnings'`, plus the rate config the drawer's
 * rate-type selector resolved to (see module docblock). Never sends
 * `is_pre_tax` -- the backend defaults it to `false` and no computation reads
 * it (see module docblock).
 */
export function buildCreatePayItemInput(
  entityInput: DataCentreEntityInput,
  draft: Omit<AllowanceDraft, 'id'>,
): CreatePayItemInput {
  return {
    ...entityInput,
    category: 'earnings',
    name: draft.name,
    ...createRateFields(draft.rate),
  };
}

/**
 * Builds the `payroll.updatePayItem` input for an existing allowance edit —
 * name + the full rate config (#22820), never touching the item's statutory
 * fields (a partial update; the backend leaves any field absent from the
 * input unchanged — `rate_type`/`rate_cents`/`rate_bps` are always present
 * here, so an edit always overwrites the item's rate to match the drawer's
 * current selection).
 */
export function buildUpdatePayItemInput(
  entityInput: DataCentreEntityInput,
  draft: AllowanceDraft & { id: string },
): UpdatePayItemInput {
  return {
    ...entityInput,
    pay_item_id: draft.id,
    name: draft.name,
    ...updateRateFields(draft.rate),
  };
}

/**
 * Builds the `payroll.createPayItem` input for a brand-new deduction (#22834)
 * — mirrors {@link buildCreatePayItemInput} exactly, `category: 'deduction'`
 * instead of `'earnings'`, reusing the SAME {@link createRateFields} helper
 * (no deduction-specific rate mapping exists, see module docblock).
 */
export function buildCreateDeductionPayItemInput(
  entityInput: DataCentreEntityInput,
  draft: Omit<DeductionDraft, 'id'>,
): CreatePayItemInput {
  return {
    ...entityInput,
    category: 'deduction',
    name: draft.name,
    ...createRateFields(draft.rate),
  };
}

/**
 * Builds the `payroll.updatePayItem` input for an existing deduction edit
 * (#22834) — mirrors {@link buildUpdatePayItemInput} exactly, name + the
 * full rate config via the SAME {@link updateRateFields} helper, never
 * touching the item's statutory fields.
 */
export function buildUpdateDeductionPayItemInput(
  entityInput: DataCentreEntityInput,
  draft: DeductionDraft & { id: string },
): UpdatePayItemInput {
  return {
    ...entityInput,
    pay_item_id: draft.id,
    name: draft.name,
    ...updateRateFields(draft.rate),
  };
}
